import type { Lake, Locale, SpotListItem } from "@divespots/contracts";
import { pickLocale } from "@divespots/contracts";

/** Lowercase, strip diacritics ("Zürich" matches "zurich"), collapse whitespace. */
export function normalize(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Filters spots by name, locality, water body and (localised) lake name. Every query word
 * must match somewhere. Name matches rank first, prefix matches before substring matches.
 */
export function searchSpots(
  spots: readonly SpotListItem[],
  query: string,
  locale: Locale,
  lakes: readonly Lake[] = [],
  limit = 8,
): SpotListItem[] {
  const words = normalize(query).split(" ").filter(Boolean);
  if (words.length === 0) return [];
  const lakeName = new Map(lakes.map((l) => [l.slug, pickLocale(l.name, locale) ?? ""]));

  const scored: Array<{ spot: SpotListItem; score: number }> = [];
  for (const spot of spots) {
    const name = normalize(spot.name);
    const haystack = [
      name,
      normalize(spot.locality),
      normalize(spot.waterBody),
      normalize(lakeName.get(spot.lakeSlug ?? "") ?? ""),
    ];
    if (!words.every((w) => haystack.some((h) => h.includes(w)))) continue;
    let score = 0;
    for (const w of words) {
      if (name.startsWith(w)) score += 3;
      else if (name.includes(w)) score += 2;
      else score += 1;
    }
    scored.push({ spot, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.spot.name.localeCompare(b.spot.name))
    .slice(0, limit)
    .map((s) => s.spot);
}
